import moment from 'moment';
import FleetHelpers, {FLEET_BASE, extractFleetError, conflictText, saveXlsxBlob, fleetFetchAllPages} from './FleetHelpers';

/**
 * Trip board / detail / stop visibility / reports (T021/T029/T035/T037/T040).
 * Board: GET /api/v1/fleet/trips?date=&route_id=&status=.
 * Detail: GET /trips/{id}/detail. Lifecycle: PUT /trips/{id}/status
 * ({action:start|cancel|reopen}), POST /trips/{id}/close. Crew: POST
 * /trips/{id}/drivers, PUT /trips/{id}/drivers/{tripDriverId}/release.
 * Move: POST /trips/{id}/parcels/move. Stop views: GET /stops/incoming,
 * GET /trips/{trip}/stops/{tripStop}/reconciliation. Reports: /reports/*.
 */
export default {
    mixins: [FleetHelpers],
    data: () => ({
        isLoading: false,
        btnLoading: false,

        // Board
        trips: [],
        boardDate: moment(new Date()).format('YYYY-MM-DD'),
        boardRouteId: '',
        boardStatus: '',
        boardApproved: '',   // '' all · '0' pending · '1' approved
        boardRoutes: [],
        pagination: {},
        offset: 10,
        per_page: 30,
        count: 0,

        tripStatusOptions: [
            {id: '', name: 'ທັງໝົດ'},
            {id: 'planned', name: 'ວາງແຜນແລ້ວ'},
            {id: 'in_progress', name: 'ກຳລັງແລ່ນ'},
            {id: 'closed', name: 'ປິດຖ້ຽວແລ້ວ'},
            {id: 'cancelled', name: 'ຍົກເລີກ'},
        ],

        // Detail
        trip: null,
        tripLoading: false,
        stopNames: {},        // "type:id" -> facility name (from route detail)
        stopGeo: {},          // "type:id" -> {lat, lng} (from route detail, for the trip map)
        stopNamesRouteId: null, // route the two maps above were resolved for

        conflicts: [],

        // Close dialog
        closeValue: {
            end_odometer_km: '',
            start_odometer_km: '',
            remark: '',
            force: false,
        },
        closeSummary: null,
        closeWarnings: [],
        // Set from a 422 TRIP_HAS_PARCELS_ON_TRUCK — count still on the truck,
        // reveals the manager-only force-close confirm.
        closeBlockedOnTruck: 0,

        // Volume recommendation (per trip)
        volumeRecommendation: null,
        volumeLoading: false,

        // Reconciliation per stop
        reconciliation: null,
        reconciliationLoading: false,

        // Move-parcels dialog
        moveTargetTripId: '',
        moveParcelIds: [],
    }),
    methods: {
        /** ---------------- Board ---------------- */
        FetchTrips() {
            this.isLoading = true;
            this.$http.get(`${FLEET_BASE}/trips`, {
                params: {
                    date: this.boardDate || undefined,
                    route_id: this.boardRouteId || undefined,
                    status: this.boardStatus || undefined,
                    approved: this.boardApproved !== '' ? this.boardApproved : undefined,
                    page: this.pagination.current_page || 1,
                    per_page: this.per_page,
                },
            }).then((res) => {
                this.trips = res.data.data || [];
                this.pagination = res.data.meta || {};
                this.count = this.pagination.total || this.trips.length;
                this.isLoading = false;
            }).catch(() => {
                this.isLoading = false;
            });
        },
        FilterTrips() {
            this.pagination.current_page = 1;
            this.FetchTrips();
        },
        FetchBoardRoutes() {
            fleetFetchAllPages(this.$http, `${FLEET_BASE}/routes`).then((routes) => {
                this.boardRoutes = routes;
                this.boardRoutes.unshift({id: '', name: 'ທັງໝົດ'});
            }).catch(() => {
            });
        },

        /** ---------------- Detail ---------------- */
        /**
         * @param {boolean} silent background poll: keep the rendered page up
         *   (the loading flag swaps it for a spinner) and stay quiet on error.
         *   A refresh running every few seconds must not flash the layout, and
         *   one dropped request must not stack error toasts on screen.
         */
        FetchTripDetail(tripId, silent = false) {
            if (!silent) this.tripLoading = true;
            return this.$http.get(`${FLEET_BASE}/trips/${tripId}/detail`).then((res) => {
                this.trip = res.data.data;
                this.tripLoading = false;
                this.ResolveStopNames();
                return this.trip;
            }).catch((error) => {
                this.tripLoading = false;
                if (!silent) {
                    this.$notification.error({message: extractFleetError(error, 'ບໍ່ພົບຂໍ້ມູນຖ້ຽວ')});
                }
                return Promise.reject(error);
            });
        },
        // Trip stops carry no facility names — resolve them from the route detail
        // (stops were snapshotted from it); fall back to "type #id". The same
        // payload carries facility lat/lng, kept in stopGeo for the trip map.
        ResolveStopNames() {
            if (!this.trip || !this.trip.route_id) return;
            // Names and pins come from the route, which cannot change under a
            // running trip (stops are snapshotted at round creation). Resolve
            // once per route so an auto-refreshing detail page doesn't fire a
            // second request every tick for an answer it already has.
            if (this.stopNamesRouteId === this.trip.route_id) return;
            this.stopNamesRouteId = this.trip.route_id;
            this.$http.get(`${FLEET_BASE}/routes/${this.trip.route_id}`).then((res) => {
                const route = res.data.data || {};
                const map = {};
                const geo = {};
                const keep = (key, facility) => {
                    if (!facility) return;
                    if (facility.name) map[key] = facility.name;
                    if (Number(facility.lat) && Number(facility.lng)) {
                        geo[key] = {lat: Number(facility.lat), lng: Number(facility.lng)};
                    }
                };
                if (route.start_stop_type) {
                    keep(`${route.start_stop_type}:${route.start_stop_id}`, route.start_stop_facility);
                }
                (route.stops || []).forEach((stop) => {
                    keep(`${stop.stop_type}:${stop.stop_id}`, stop.facility);
                });
                this.stopNames = map;
                this.stopGeo = geo;
            }).catch(() => {
                // Release the claim so the next fetch retries — otherwise one
                // failed call leaves every stop showing "type #id" for good.
                this.stopNamesRouteId = null;
            });
        },
        StopName(stop) {
            if (!stop) return '...';
            return this.stopNames[`${stop.stop_type}:${stop.stop_id}`]
                || `${this.FleetStopTypeLabel(stop.stop_type)} #${stop.stop_id}`;
        },

        /** ---------------- Lifecycle (T035) ---------------- */
        TripStatusAction(tripId, action, extra = {}) {
            this.btnLoading = true;
            return this.$http.put(`${FLEET_BASE}/trips/${tripId}/status`, {action, ...extra}).then((res) => {
                this.btnLoading = false;
                this.$notification.success({
                    message: {
                        start: 'ເລີ່ມຖ້ຽວແລ້ວ',
                        cancel: 'ຍົກເລີກຖ້ຽວແລ້ວ',
                        reopen: 'ເປີດຖ້ຽວຄືນແລ້ວ',
                        approve: 'ອະນຸມັດຖ້ຽວແລ້ວ',
                        unapprove: 'ຍົກເລີກການອະນຸມັດແລ້ວ',
                    }[action] || 'ສຳເລັດ',
                });
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        /** ---------------- Copy day + approval ---------------- */
        // Dry-run: what a copy from sourceDate → targetDate would create/skip.
        PreviewCopyDay(sourceDate, targetDate) {
            return this.$http.get(`${FLEET_BASE}/trips/copy-day/preview`, {
                params: {source_date: sourceDate, target_date: targetDate},
            }).then((res) => res.data.data).catch((error) => {
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // Execute the copy — the created trips land PENDING (need approval).
        CopyDay(sourceDate, targetDate) {
            this.btnLoading = true;
            return this.$http.post(`${FLEET_BASE}/trips/copy-day`, {
                source_date: sourceDate,
                target_date: targetDate,
            }).then((res) => {
                this.btnLoading = false;
                return res.data; // {data:[...trips], created_count, skipped, ...}
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // Approve/un-approve one pending trip (reuses the status endpoint).
        ApproveTrip(tripId, approved = true) {
            return this.TripStatusAction(tripId, approved ? 'approve' : 'unapprove');
        },
        // One-click "create the return (ຂາກັບ) leg" for an outbound trip that
        // has no pair (legacy rows). New rounds already auto-create their return.
        CreateReturnTrip(tripId) {
            this.btnLoading = true;
            return this.$http.post(`${FLEET_BASE}/trips/${tripId}/return`).then((res) => {
                this.btnLoading = false;
                this.$notification.success({message: 'ສ້າງຖ້ຽວຂາກັບແລ້ວ'});
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error, 'ສ້າງຖ້ຽວຂາກັບບໍ່ສຳເລັດ')});
                return Promise.reject(error);
            });
        },
        // FleetDirectionLabel/Color/Icon live in FleetHelpers (mixed in above)
        // so every surface renders the leg identically. Do NOT re-declare a
        // local copy here — a component-level method shadows the shared one.

        // Bulk enable/disable pending trips.
        ApproveTripsBatch(tripIds, approved = true) {
            this.btnLoading = true;
            return this.$http.post(`${FLEET_BASE}/trips/approve-batch`, {
                trip_ids: tripIds,
                approved,
            }).then((res) => {
                this.btnLoading = false;
                return res.data.data; // {updated, skipped}
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },

        // Edit truck/timetable while still planned (FR-012); conflicts re-checked.
        UpdateTrip(tripId, payload) {
            this.btnLoading = true;
            return this.$http.put(`${FLEET_BASE}/trips/${tripId}`, payload).then((res) => {
                this.btnLoading = false;
                const conflicts = res.data.conflicts || [];
                if (conflicts.length) {
                    this.$notification.warning({message: conflicts.map(conflictText).join(' / '), duration: 6000});
                }
                this.$notification.OpenNotification_EditItem_OnSuccess('top-right', 'dark', 3000);
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        CloseTrip(tripId) {
            this.btnLoading = true;
            const payload = {
                // Optional on a FORCE close (unknowable on an abandoned trip) —
                // and Number('') is 0, so an empty field must become undefined,
                // never a fake 0 km reading.
                end_odometer_km: this.closeValue.end_odometer_km === '' || this.closeValue.end_odometer_km === null
                    ? undefined : Number(this.closeValue.end_odometer_km),
                start_odometer_km: this.closeValue.start_odometer_km === '' || this.closeValue.start_odometer_km === null
                    ? undefined : Number(this.closeValue.start_odometer_km),
                remark: this.closeValue.remark || undefined,
                force: this.closeValue.force ? true : undefined,
            };
            return this.$http.post(`${FLEET_BASE}/trips/${tripId}/close`, payload).then((res) => {
                this.btnLoading = false;
                this.closeBlockedOnTruck = 0;
                this.closeSummary = res.data.summary || null;
                this.closeWarnings = res.data.warnings || [];
                this.$notification.OpenNotification_CloseItem_OnSuccess('top-right', 'dark', 3000);
                return res.data;
            }).catch((error) => {
                this.btnLoading = false;
                // The leftover gate: reveal the manager-only force confirm and
                // keep the dialog open instead of just toasting the error.
                const errors = error && error.response && error.response.data && error.response.data.errors;
                if (errors && errors.trip && errors.trip.includes('TRIP_HAS_PARCELS_ON_TRUCK')) {
                    this.closeBlockedOnTruck = Number(errors.on_truck && errors.on_truck[0]) || 1;
                }
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },

        /** ---------------- Crew ---------------- */
        AddTripDriver(tripId, driverId, driverRole = 'crew') {
            this.btnLoading = true;
            return this.$http.post(`${FLEET_BASE}/trips/${tripId}/drivers`, {
                driver_id: driverId,
                driver_role: driverRole,
            }).then((res) => {
                this.btnLoading = false;
                const conflicts = res.data.conflicts || [];
                if (conflicts.length) {
                    this.$notification.warning({message: conflicts.map(conflictText).join(' / '), duration: 6000});
                }
                this.$notification.success({message: 'ເພີ່ມຄົນຂັບແລ້ວ'});
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        ReleaseTripDriver(tripId, tripDriverId) {
            return this.$http.put(`${FLEET_BASE}/trips/${tripId}/drivers/${tripDriverId}/release`).then((res) => {
                this.$notification.success({message: 'ປົດຄົນຂັບອອກຈາກຖ້ຽວແລ້ວ'});
                return res.data.data;
            }).catch((error) => {
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },

        /** ---------------- Manifest move ---------------- */
        MoveParcels(tripId) {
            this.btnLoading = true;
            return this.$http.post(`${FLEET_BASE}/trips/${tripId}/parcels/move`, {
                target_trip_id: this.moveTargetTripId,
                parcel_in_shipment_ids: this.moveParcelIds,
            }).then((res) => {
                this.btnLoading = false;
                this.$notification.success({
                    message: `ຍ້າຍພັດສະດຸ ${res.data.data.moved} ລາຍການ ໄປຖ້ຽວປາຍທາງແລ້ວ`,
                });
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },

        /** ---------------- Volume recommendation ---------------- */
        FetchVolumeRecommendation(tripId) {
            this.volumeLoading = true;
            return this.$http.get(`${FLEET_BASE}/trips/${tripId}/volume-recommendation`).then((res) => {
                this.volumeRecommendation = res.data.data || null;
                this.volumeLoading = false;
                return this.volumeRecommendation;
            }).catch(() => {
                this.volumeLoading = false;
            });
        },

        /** ---------------- Stop visibility (T029) ---------------- */
        FetchStopsIncoming(params) {
            return this.$http.get(`${FLEET_BASE}/stops/incoming`, {params});
        },
        // `search` filters by bill number SERVER-side: a main-line stop can hold
        // 1000+ pieces, so narrowing here is what keeps the payload sane rather
        // than shipping everything and hiding rows in the browser.
        // `silent` is for the page's auto-refresh tick: it does NOT raise the
        // loading flag (which would blank the table and collapse the drill on
        // every poll) and does NOT toast on error — a transient failure mid-poll
        // is not something to interrupt the operator over, the next tick retries.
        FetchStopReconciliation(tripId, tripStopId, search, silent) {
            if (!silent) {
                this.reconciliationLoading = true;
            }
            const params = search ? {search} : {};
            return this.$http.get(`${FLEET_BASE}/trips/${tripId}/stops/${tripStopId}/reconciliation`, {params}).then((res) => {
                this.reconciliation = res.data.data || null;
                this.reconciliationLoading = false;
                return this.reconciliation;
            }).catch((error) => {
                this.reconciliationLoading = false;
                if (!silent) {
                    this.$notification.error({message: extractFleetError(error)});
                    return Promise.reject(error);
                }
                return null;
            });
        },

        // stop-load-ledger: per-stop IN/OUT + running on-board load for a trip.
        FetchStopLoadLedger(tripId) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}/stop-load-ledger`);
        },
        // Drill-in: the parcels behind one movement (in|out|on_board_*) of a stop.
        FetchStopMovements(tripId, tripStopId, params) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}/stops/${tripStopId}/movements`, {params});
        },
        // The carved-out off-route bucket, expanded: the mis-loaded parcels on a
        // trip with their dest / load / drop detail ("find where is it").
        FetchOffRouteParcels(tripId) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}/off-route`);
        },
        // Searchable full parcel list on a trip (by bill/parcel number) — the
        // "where are the parcels on this trip" lookup, independent of stop visits.
        FetchTripParcels(tripId, params) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}/parcels`, {params});
        },
        // US2, one trip: where its truck is right now, for the trip-detail map.
        // Resolves {data: null} when GPS is off, the trip is not running, or it
        // has never reported — the map simply shows no truck.
        FetchTripLivePosition(tripId) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}/live-position`);
        },

        /** ---------------- Reports (T037 / T040) ---------------- */
        // Snapshot only: the backend has no date parameter — it always returns
        // the CURRENT pending volume (the response carries its own `date`).
        FetchVolumeDashboard() {
            return this.$http.get(`${FLEET_BASE}/reports/volume-dashboard`);
        },
        FetchDailyReport(params) {
            return this.$http.get(`${FLEET_BASE}/reports/daily`, {params});
        },
        ExportDailyReport(params) {
            return this.$http.get(`${FLEET_BASE}/reports/daily/export`, {
                params,
                responseType: 'blob',
            }).then((response) => {
                saveXlsxBlob(response, `ລາຍງານຂົນສົ່ງລາຍວັນ ${params.from} ຫາ ${params.to}.xlsx`);
            });
        },
        FetchTripReport(tripId) {
            return this.$http.get(`${FLEET_BASE}/reports/trips/${tripId}`);
        },
        // US3 (001-fleet-gps-tracking): per-stop planned-vs-actual timetable
        // variance + per-route/day aggregate. Self-hides on an old backend.
        FetchTimetableVariance(params) {
            return this.$http.get(`${FLEET_BASE}/reports/timetable-variance`, {params});
        },
        // FR-023: cross-trip driver-reported problems (ops triage list).
        FetchProblemsReport(params) {
            return this.$http.get(`${FLEET_BASE}/reports/problems`, {params});
        },
    },
};
